import { DEFAULT_DOCUMENT_VIEWPORT, getActivePageSceneNodes, getAllSceneNodes, getSceneNodeChildren } from "@/document/model/document";
import type { CurvaDocument, DocumentViewport } from "@/document/model/document";
import { frameAutoLayoutMode, frameFlexLayout, type FrameNode, type SceneNode } from "@/document/model/nodes";
import type { PrimitiveKind } from "@/document/model/primitives";
import { calculateFlexFrameChildBounds } from "@/document/operations/autoLayout";
import { isNodeLockedForEditing } from "@/document/operations/nodeLocking";
import { getSelectionBounds, hitTestResizeHandle, hitTestRotationHandle, normalizeRect, offsetBounds, type ResizeHandle, type RotationHandle } from "@/editor/tools/hitTest";
import { snapBoundsToObjects, type ObjectSnapCandidate, type ObjectSnapGuide } from "@/editor/tools/objectSnap";
import { guideSnapCandidates, snapBoundsToGuides } from "@/editor/tools/guideSnap";
import { screenToDocument, clampZoom } from "@/editor/state/viewportStore";
import { createSceneTransformCache, nodeRotationOrigin, nodeSelectionDragDeltaToLocal, nodeSelectionPointToLocal, nodeSelfRotation, type SceneTransformCache } from "@/document/operations/nodeWorldTransform";
import { inverseRotatePoint, normalizeDegrees, rectCenter, rotatePoint, rotationOriginForBounds, transformRotationOrigin } from "@/lib/math/rotation";
import type { Rect } from "@/lib/math/rect";
import { createDefaultCamera } from "@/renderer/camera";
import type { RenderPreview, RotationOriginGizmoOverlay, VectorSmartTransformOverlay } from "@/renderer/overlayTypes";
import type { ActiveTool } from "./TopBar";
import type { EditorSelectionState } from "@/commands/Command";
import type { DragState } from "./canvasWorkspaceTypes";

export function dragOffsetFromPreview(drag: DragState, previews: RenderPreview[]): { x: number; y: number } | undefined {
  for (const preview of previews) {
    const startBounds = drag.startBoundsById.get(preview.nodeId);
    if (startBounds) {
      return {
        x: preview.bounds.x - startBounds.x,
        y: preview.bounds.y - startBounds.y,
      };
    }
  }

  return undefined;
}

export function constrainDragDeltaToAxis(dx: number, dy: number): { dx: number; dy: number; axis: "x" | "y" } {
  if (Math.abs(dx) >= Math.abs(dy)) {
    return { dx, dy: 0, axis: "x" };
  }
  return { dx: 0, dy, axis: "y" };
}

/** Original selection bounds used as snap targets while alt-dragging a copy. */
export function copySourceSnapCandidates(drag: DragState): ObjectSnapCandidate[] {
  return drag.nodeIds.flatMap(nodeId => {
    const bounds = drag.startBoundsById.get(nodeId);
    return bounds ? [{ nodeId, bounds }] : [];
  });
}

export function movePreviewsForDrag(
  drag: DragState,
  dx: number,
  dy: number,
  copy: boolean,
  snapToPixelGrid: boolean,
  snapToObjects: boolean,
  snapToGuides: boolean,
  zoom: number,
  document: CurvaDocument,
  activePageId: string | undefined,
  selectedNodeIds: string[],
  constrainAxis = false,
): { previews: RenderPreview[]; guides: ObjectSnapGuide[] } {
  const constrained = constrainAxis ? constrainDragDeltaToAxis(dx, dy) : undefined;
  const moveDx = constrained?.dx ?? dx;
  const moveDy = constrained?.dy ?? dy;
  const mode: RenderPreview["mode"] = copy ? "copy" : "move";
  const previews = drag.nodeIds.flatMap(nodeId => {
    const bounds = drag.startBoundsById.get(nodeId);
    if (!bounds) {
      return [];
    }

    const localDelta = nodeSelectionDragDeltaToLocal(document, nodeId, { x: moveDx, y: moveDy });
    const nextBounds = offsetBounds(bounds, localDelta.x, localDelta.y);
    return [{
      nodeId,
      mode,
      bounds: snapToPixelGrid ? snapBoundsOriginToPixelGrid(nextBounds) : nextBounds,
    }];
  });
  const movingBounds = previewSelectionBoundsFromPreviews(previews);
  const objectCandidates = [
    ...(drag.objectSnapCandidates ?? []),
    ...(copy ? copySourceSnapCandidates(drag) : []),
  ];
  const objectSnap = snapToObjects && movingBounds && objectCandidates.length > 0
    ? snapBoundsToObjects(movingBounds, objectCandidates, zoom)
    : undefined;
  const objectDx = constrained?.axis === "y" ? 0 : (objectSnap?.dx ?? 0);
  const objectDy = constrained?.axis === "x" ? 0 : (objectSnap?.dy ?? 0);
  const afterObjectSnapBounds = movingBounds && (objectDx !== 0 || objectDy !== 0)
    ? offsetBounds(movingBounds, objectDx, objectDy)
    : movingBounds;
  const guideSnap = snapToGuides && afterObjectSnapBounds
    ? snapBoundsToGuides(afterObjectSnapBounds, guideSnapCandidates(document, activePageId, selectedNodeIds), zoom)
    : undefined;
  const guideDx = constrained?.axis === "y" ? 0 : (guideSnap?.dx ?? 0);
  const guideDy = constrained?.axis === "x" ? 0 : (guideSnap?.dy ?? 0);
  const totalDx = objectDx + guideDx;
  const totalDy = objectDy + guideDy;
  const guides = [
    ...(objectSnap?.guides ?? []),
    ...(guideSnap?.guides ?? []),
  ].filter(guide => {
    if (!constrained) {
      return true;
    }
    // Vertical guides lock X; horizontal guides lock Y.
    return constrained.axis === "x" ? guide.orientation === "vertical" : guide.orientation === "horizontal";
  });

  return {
    previews: totalDx !== 0 || totalDy !== 0
      ? previews.map(preview => ({ ...preview, bounds: offsetBounds(preview.bounds, totalDx, totalDy) }))
      : previews,
    guides,
  };
}

export function flexReorderPreviewForDrag(
  document: CurvaDocument,
  drag: DragState,
  dx: number,
  dy: number,
): { previews: RenderPreview[]; guides: ObjectSnapGuide[]; target: { parentFrameId: string; index: number } } | undefined {
  if (drag.nodeIds.length !== 1) {
    return undefined;
  }

  const nodeId = drag.nodeIds[0]!;
  const startBounds = drag.startBoundsById.get(nodeId);
  const parentFrame = startBounds ? directFlexParentFrame(document, nodeId) : undefined;
  if (!startBounds || !parentFrame) {
    return undefined;
  }

  const currentIndex = parentFrame.children.findIndex(child => child.id === nodeId);
  if (currentIndex < 0) {
    return undefined;
  }

  const direction = frameFlexLayout(parentFrame).direction;
  const axis = direction === "row" || direction === "row-reverse" ? "x" : "y";
  const reverse = direction === "row-reverse" || direction === "column-reverse";
  const draggedCenter = axis === "x"
    ? startBounds.x + dx + startBounds.width / 2
    : startBounds.y + dy + startBounds.height / 2;
  const siblings = parentFrame.children.filter(child => child.id !== nodeId);
  let targetIndex = 0;
  for (const sibling of siblings) {
    const siblingMidline = axis === "x"
      ? sibling.bounds.x + sibling.bounds.width / 2
      : sibling.bounds.y + sibling.bounds.height / 2;
    if (reverse ? draggedCenter < siblingMidline : draggedCenter > siblingMidline) {
      targetIndex += 1;
    }
  }

  const orderedIds = parentFrame.children.map(child => child.id).filter(id => id !== nodeId);
  orderedIds.splice(targetIndex, 0, nodeId);
  const orderedChildren = orderedIds.flatMap(id => {
    const child = parentFrame.children.find(child => child.id === id);
    return child ? [child] : [];
  });
  const finalBoundsById = calculateFlexFrameChildBounds({ ...parentFrame, children: orderedChildren });
  const previews = orderedChildren.flatMap(child => {
    const finalBounds = finalBoundsById.get(child.id);
    const bounds = child.id === nodeId ? offsetBounds(startBounds, dx, dy) : finalBounds;
    if (!bounds) {
      return [];
    }
    return [{
      nodeId: child.id,
      mode: "move" as const,
      bounds,
    }];
  });

  return {
    previews,
    guides: [],
    target: { parentFrameId: parentFrame.id, index: targetIndex },
  };
}

export function directFlexParentFrame(document: CurvaDocument, nodeId: string): FrameNode | undefined {
  for (const page of document.pages) {
    const parent = directFlexParentFrameInNodes(page.children, nodeId);
    if (parent) {
      return parent;
    }
  }
  return undefined;
}

export function previewSelectionBoundsFromPreviews(previews: RenderPreview[]): Rect | undefined {
  const bounds = previews.filter(preview => preview.nodeId !== "__marquee__").map(preview => preview.bounds);
  if (bounds.length === 0) {
    return undefined;
  }

  const left = Math.min(...bounds.map(bound => Math.min(bound.x, bound.x + bound.width)));
  const top = Math.min(...bounds.map(bound => Math.min(bound.y, bound.y + bound.height)));
  const right = Math.max(...bounds.map(bound => Math.max(bound.x, bound.x + bound.width)));
  const bottom = Math.max(...bounds.map(bound => Math.max(bound.y, bound.y + bound.height)));
  return { x: left, y: top, width: right - left, height: bottom - top };
}

export function applySelectionClick(selection: string[], nodeId: string, additive: boolean): string[] {
  if (!additive) {
    return [nodeId];
  }

  return selection.includes(nodeId) ? selection.filter(id => id !== nodeId) : [...selection, nodeId];
}

export function applySelectionPointerDown(selection: string[], nodeId: string, additive: boolean): string[] {
  if (!additive && selection.includes(nodeId)) {
    return selection;
  }

  return applySelectionClick(selection, nodeId, additive);
}

export function toggleEdgeSelection(selection: string[], edgeId: string, additive: boolean): string[] {
  if (!additive) {
    return [edgeId];
  }

  return selection.includes(edgeId) ? selection.filter(id => id !== edgeId) : [...selection, edgeId];
}

export function toggleVertexSelection(selection: string[], vertexId: string): string[] {
  return selection.includes(vertexId) ? selection.filter(id => id !== vertexId) : [...selection, vertexId];
}

export function mergeSelection(current: string[], incoming: string[]): string[] {
  return [...current, ...incoming.filter(id => !current.includes(id))];
}

export function isDoubleClickContainer(node: SceneNode): boolean {
  return node.type === "frame" || node.type === "group" || node.type === "boolean" || node.type === "component" || node.type === "componentVariant" || node.type === "instance";
}

export function snapPointToAngle(from: { x: number; y: number }, to: { x: number; y: number }): { x: number; y: number } {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const distance = Math.hypot(dx, dy);
  if (distance <= 0.0001) {
    return to;
  }

  const snapStep = Math.PI / 12;
  const snappedAngle = Math.round(Math.atan2(dy, dx) / snapStep) * snapStep;
  return {
    x: from.x + Math.cos(snappedAngle) * distance,
    y: from.y + Math.sin(snappedAngle) * distance,
  };
}

export function shapeDrawBounds(
  start: { x: number; y: number },
  current: { x: number; y: number },
  kind: PrimitiveKind,
  constrained: boolean,
  centered: boolean,
  snapToPixelGrid: boolean,
  pixelSnapMode: "half" | "whole" = "half",
): Rect {
  const snapPoint = pixelSnapMode === "whole" ? snapPointToWholePixelGrid : snapPointToPixelGrid;
  const startPoint = snapToPixelGrid ? snapPoint(start) : start;
  const currentPoint = snapToPixelGrid ? snapPoint(current) : current;
  let endPoint = currentPoint;

  if (constrained) {
    if (kind === "line") {
      endPoint = snapToPixelGrid ? snapPoint(snapPointToAngle(startPoint, currentPoint)) : snapPointToAngle(startPoint, currentPoint);
    } else {
      const dx = currentPoint.x - startPoint.x;
      const dy = currentPoint.y - startPoint.y;
      const size = Math.max(Math.abs(dx), Math.abs(dy));
      endPoint = {
        x: startPoint.x + Math.sign(dx || 1) * size,
        y: startPoint.y + Math.sign(dy || 1) * size,
      };
    }
  }

  if (centered) {
    const dx = endPoint.x - startPoint.x;
    const dy = endPoint.y - startPoint.y;
    if (kind === "line") {
      return {
        x: startPoint.x - dx,
        y: startPoint.y - dy,
        width: dx * 2,
        height: dy * 2,
      };
    }

    return normalizeRect({
      x: startPoint.x - dx,
      y: startPoint.y - dy,
      width: dx * 2,
      height: dy * 2,
    });
  }

  if (kind === "line") {
    return {
      x: startPoint.x,
      y: startPoint.y,
      width: endPoint.x - startPoint.x,
      height: endPoint.y - startPoint.y,
    };
  }

  return normalizeRect({
    x: startPoint.x,
    y: startPoint.y,
    width: endPoint.x - startPoint.x,
    height: endPoint.y - startPoint.y,
  });
}

export function snapShapeDrawBoundsToFrame(bounds: Rect, frameBounds: Rect, zoom: number): { bounds: Rect; guides: ObjectSnapGuide[] } {
  const tolerance = 5 / Math.max(0.0001, zoom);
  const frameLeft = frameBounds.x;
  const frameRight = frameBounds.x + frameBounds.width;
  const frameTop = frameBounds.y;
  const frameBottom = frameBounds.y + frameBounds.height;
  const x1Snap = closestDrawEdgeSnap(bounds.x, [frameLeft, frameRight], tolerance);
  const x2Snap = closestDrawEdgeSnap(bounds.x + bounds.width, [frameLeft, frameRight], tolerance);
  const y1Snap = closestDrawEdgeSnap(bounds.y, [frameTop, frameBottom], tolerance);
  const y2Snap = closestDrawEdgeSnap(bounds.y + bounds.height, [frameTop, frameBottom], tolerance);
  const nextBounds = {
    x: x1Snap ?? bounds.x,
    y: y1Snap ?? bounds.y,
    width: (x2Snap ?? bounds.x + bounds.width) - (x1Snap ?? bounds.x),
    height: (y2Snap ?? bounds.y + bounds.height) - (y1Snap ?? bounds.y),
  };
  const drawTop = Math.min(nextBounds.y, nextBounds.y + nextBounds.height);
  const drawBottom = Math.max(nextBounds.y, nextBounds.y + nextBounds.height);
  const drawLeft = Math.min(nextBounds.x, nextBounds.x + nextBounds.width);
  const drawRight = Math.max(nextBounds.x, nextBounds.x + nextBounds.width);
  const guides: ObjectSnapGuide[] = [];
  addDrawSnapGuide(guides, x1Snap, "vertical", Math.min(frameTop, drawTop), Math.max(frameBottom, drawBottom));
  addDrawSnapGuide(guides, x2Snap, "vertical", Math.min(frameTop, drawTop), Math.max(frameBottom, drawBottom));
  addDrawSnapGuide(guides, y1Snap, "horizontal", Math.min(frameLeft, drawLeft), Math.max(frameRight, drawRight));
  addDrawSnapGuide(guides, y2Snap, "horizontal", Math.min(frameLeft, drawLeft), Math.max(frameRight, drawRight));

  return { bounds: nextBounds, guides };
}

function closestDrawEdgeSnap(value: number, targets: number[], tolerance: number): number | undefined {
  let best: { value: number; distance: number } | undefined;
  for (const target of targets) {
    const distance = Math.abs(target - value);
    if (distance > tolerance || (best && distance >= best.distance)) {
      continue;
    }
    best = { value: target, distance };
  }
  return best?.value;
}

function addDrawSnapGuide(
  guides: ObjectSnapGuide[],
  position: number | undefined,
  orientation: ObjectSnapGuide["orientation"],
  from: number,
  to: number,
): void {
  if (position === undefined || guides.some(guide => guide.orientation === orientation && guide.position === position)) {
    return;
  }
  guides.push({ orientation, position, from, to });
}

export function snapPointToPixelGrid(point: { x: number; y: number }): { x: number; y: number } {
  return {
    x: snapToPixelGridValue(point.x),
    y: snapToPixelGridValue(point.y),
  };
}

export function snapPointToWholePixelGrid(point: { x: number; y: number }): { x: number; y: number } {
  return {
    x: Math.round(point.x),
    y: Math.round(point.y),
  };
}

export function snapBoundsOriginToPixelGrid(bounds: Rect): Rect {
  return {
    ...bounds,
    x: snapToPixelGridValue(bounds.x),
    y: snapToPixelGridValue(bounds.y),
  };
}

export function canvasViewportBounds(canvas: HTMLCanvasElement, camera: ReturnType<typeof createDefaultCamera>): Rect {
  const scale = camera.zoom * camera.devicePixelRatio;
  return {
    x: camera.x,
    y: camera.y,
    width: canvas.width / scale,
    height: canvas.height / scale,
  };
}

export function cameraFromDocumentViewport(viewport: DocumentViewport = DEFAULT_DOCUMENT_VIEWPORT): ReturnType<typeof createDefaultCamera> {
  return {
    ...createDefaultCamera(),
    x: viewport.x,
    y: viewport.y,
    zoom: clampZoom(viewport.zoom),
  };
}

export function cameraToDocumentViewport(camera: ReturnType<typeof createDefaultCamera>): DocumentViewport {
  return {
    x: camera.x,
    y: camera.y,
    zoom: camera.zoom,
  };
}

export function isDrawableBounds(kind: PrimitiveKind, bounds: Rect): boolean {
  if (kind === "line") {
    return Math.hypot(bounds.width, bounds.height) >= 0;
  }

  return bounds.width >= 0 && bounds.height >= 0;
}

export function smartTransformSelectionKey(mode: EditorSelectionState["mode"], nodeId: string | undefined, vertexIds: string[]): string | undefined {
  return mode === "vectorEdit" && nodeId && vertexIds.length > 1 ? `${nodeId}:${vertexIds.join(",")}` : undefined;
}

export function hitTestVectorSmartTransform(
  transform: VectorSmartTransformOverlay,
  point: { x: number; y: number },
  zoom: number,
): { type: "resize"; handle: ResizeHandle } | { type: "rotate"; handle: RotationHandle } | undefined {
  const origin = rectCenter(transform.bounds);
  const rotationHit = hitTestRotationHandle(transform.bounds, point, zoom, transform.rotation, origin);
  if (rotationHit) {
    return { type: "rotate", handle: rotationHit };
  }

  const localPoint = inverseRotatePoint(point, origin, transform.rotation);
  const resizeHit = hitTestResizeHandle(transform.bounds, localPoint, zoom);
  return resizeHit ? { type: "resize", handle: resizeHit } : undefined;
}

export function boundsFromVertexPositions(vertices: Array<{ x: number; y: number }>): Rect {
  const left = Math.min(...vertices.map(vertex => vertex.x));
  const top = Math.min(...vertices.map(vertex => vertex.y));
  const right = Math.max(...vertices.map(vertex => vertex.x));
  const bottom = Math.max(...vertices.map(vertex => vertex.y));
  return { x: left, y: top, width: right - left, height: bottom - top };
}

export function constrainResizeBoundsAspectRatio(startBounds: Rect, nextBounds: Rect, handle: ResizeHandle): Rect {
  const aspectRatio = Math.max(0.0001, startBounds.width) / Math.max(0.0001, startBounds.height);
  const left = startBounds.x;
  const right = startBounds.x + startBounds.width;
  const top = startBounds.y;
  const bottom = startBounds.y + startBounds.height;
  const nextLeft = nextBounds.x;
  const nextTop = nextBounds.y;

  if (handle === "e" || handle === "w") {
    const width = nextBounds.width;
    const height = width / aspectRatio;
    return {
      x: handle === "w" ? right - width : left,
      y: (top + bottom) / 2 - height / 2,
      width,
      height,
    };
  }

  if (handle === "n" || handle === "s") {
    const height = nextBounds.height;
    const width = height * aspectRatio;
    return {
      x: (left + right) / 2 - width / 2,
      y: handle === "n" ? bottom - height : top,
      width,
      height,
    };
  }

  const anchoredRight = handle.includes("w") ? right : undefined;
  const anchoredLeft = handle.includes("e") ? left : undefined;
  const anchoredBottom = handle.includes("n") ? bottom : undefined;
  const anchoredTop = handle.includes("s") ? top : undefined;
  const widthFromPointer = nextBounds.width;
  const heightFromPointer = nextBounds.height;
  const widthFromHeight = heightFromPointer * aspectRatio;
  const heightFromWidth = widthFromPointer / aspectRatio;
  const useWidth = Math.abs(widthFromPointer - startBounds.width) >= Math.abs(widthFromHeight - startBounds.width);
  const width = useWidth ? widthFromPointer : widthFromHeight;
  const height = useWidth ? heightFromWidth : heightFromPointer;

  return {
    x: anchoredRight !== undefined ? anchoredRight - width : anchoredLeft ?? nextLeft,
    y: anchoredBottom !== undefined ? anchoredBottom - height : anchoredTop ?? nextTop,
    width,
    height,
  };
}

export function resizeBoundsAroundCenter(bounds: Rect, handle: ResizeHandle, dx: number, dy: number): Rect {
  const left = bounds.x;
  const right = bounds.x + bounds.width;
  const top = bounds.y;
  const bottom = bounds.y + bounds.height;
  const movingLeft = handle.includes("w") ? left + dx : handle.includes("e") ? left - dx : left;
  const movingRight = handle.includes("e") ? right + dx : handle.includes("w") ? right - dx : right;
  const movingTop = handle.includes("n") ? top + dy : handle.includes("s") ? top - dy : top;
  const movingBottom = handle.includes("s") ? bottom + dy : handle.includes("n") ? bottom - dy : bottom;

  return {
    x: Math.min(movingLeft, movingRight),
    y: Math.min(movingTop, movingBottom),
    width: Math.abs(movingRight - movingLeft),
    height: Math.abs(movingBottom - movingTop),
  };
}

export function constrainResizeBoundsAspectRatioAroundCenter(startBounds: Rect, nextBounds: Rect, handle: ResizeHandle): Rect {
  const aspectRatio = Math.max(0.0001, startBounds.width) / Math.max(0.0001, startBounds.height);
  const center = rectCenter(startBounds);
  const widthFromPointer = nextBounds.width;
  const heightFromPointer = nextBounds.height;
  const widthFromHeight = heightFromPointer * aspectRatio;
  const heightFromWidth = widthFromPointer / aspectRatio;

  let width: number;
  let height: number;
  if (handle === "e" || handle === "w") {
    width = widthFromPointer;
    height = heightFromWidth;
  } else if (handle === "n" || handle === "s") {
    width = widthFromHeight;
    height = heightFromPointer;
  } else {
    const useWidth = Math.abs(widthFromPointer - startBounds.width) >= Math.abs(widthFromHeight - startBounds.width);
    width = useWidth ? widthFromPointer : widthFromHeight;
    height = useWidth ? heightFromWidth : heightFromPointer;
  }

  return {
    x: center.x - width / 2,
    y: center.y - height / 2,
    width,
    height,
  };
}

export function transformPointBetweenRotatedBounds(point: { x: number; y: number }, fromBounds: Rect, toBounds: Rect, rotation: number): { x: number; y: number } {
  const fromCenter = rectCenter(fromBounds);
  const toCenter = rectCenter(toBounds);
  const unrotated = inverseRotatePoint(point, fromCenter, rotation);
  const scaleX = fromBounds.width === 0 ? 1 : toBounds.width / fromBounds.width;
  const scaleY = fromBounds.height === 0 ? 1 : toBounds.height / fromBounds.height;
  const transformed = {
    x: toBounds.x + (unrotated.x - fromBounds.x) * scaleX,
    y: toBounds.y + (unrotated.y - fromBounds.y) * scaleY,
  };
  return rotatePoint(transformed, toCenter, rotation);
}

export function snapDegrees(degrees: number, step: number): number {
  return normalizeDegrees(Math.round(degrees / step) * step);
}

export function previewSelectionBounds(document: CurvaDocument, selectedNodeIds: string[], previews: RenderPreview[]): Rect | undefined {
  if (previews.length === 0) {
    return getSelectionBounds(document, selectedNodeIds);
  }

  const previewById = new Map(previews.filter(preview => preview.nodeId !== "__marquee__").map(preview => [preview.nodeId, preview.bounds] as const));
  const bounds = selectedNodeIds.flatMap(nodeId => {
    const preview = previewById.get(nodeId);
    if (preview) {
      return [preview];
    }

    const node = getAllSceneNodes(document).find(candidate => candidate.id === nodeId);
    return node ? [node.bounds] : [];
  });
  if (bounds.length === 0) {
    return undefined;
  }

  const left = Math.min(...bounds.map(bound => Math.min(bound.x, bound.x + bound.width)));
  const top = Math.min(...bounds.map(bound => Math.min(bound.y, bound.y + bound.height)));
  const right = Math.max(...bounds.map(bound => Math.max(bound.x, bound.x + bound.width)));
  const bottom = Math.max(...bounds.map(bound => Math.max(bound.y, bound.y + bound.height)));
  return { x: left, y: top, width: right - left, height: bottom - top };
}

export function selectionBoundsForTransforms(document: CurvaDocument, selectedNodeIds: string[], previews: RenderPreview[]): Rect | undefined {
  if (selectedNodeIds.length !== 1) {
    return previewSelectionBounds(document, selectedNodeIds, previews);
  }

  const nodeId = selectedNodeIds[0]!;
  const preview = previews.find(candidate => candidate.nodeId === nodeId);
  if (preview) {
    return preview.bounds;
  }

  return getAllSceneNodes(document).find(node => node.id === nodeId)?.bounds;
}

export function selectionRotationForNodes(document: CurvaDocument, selectedNodeIds: string[]): number {
  if (selectedNodeIds.length !== 1) {
    return 0;
  }

  return getAllSceneNodes(document).find(node => node.id === selectedNodeIds[0])?.transform.rotation ?? 0;
}

export function selectionRotationOriginForNodes(document: CurvaDocument, selectedNodeIds: string[], fallbackBounds: Rect | undefined): { x: number; y: number } | undefined {
  if (selectedNodeIds.length !== 1) {
    return fallbackBounds ? rectCenter(fallbackBounds) : undefined;
  }

  const node = getAllSceneNodes(document).find(candidate => candidate.id === selectedNodeIds[0]);
  if (!node) {
    return fallbackBounds ? rectCenter(fallbackBounds) : undefined;
  }

  return rotationOriginForBounds(fallbackBounds ?? node.bounds, node.bounds, node.transform);
}

export function rotationOriginOverlay(
  document: CurvaDocument,
  visibleNodeId: string | undefined,
  selectedNodeIds: string[],
  hovered: boolean,
  active: boolean,
  preview: { nodeId: string; x: number; y: number; snapPoints: Array<{ x: number; y: number }> } | undefined,
  previews: RenderPreview[] = [],
): RotationOriginGizmoOverlay | undefined {
  const nodeId = preview?.nodeId ?? visibleNodeId;
  if (!nodeId || selectedNodeIds.length !== 1 || selectedNodeIds[0] !== nodeId) {
    return undefined;
  }

  const node = getAllSceneNodes(document).find(candidate => candidate.id === nodeId);
  if (!node || node.transform.rotation === 0) {
    return undefined;
  }

  const transformCache = createSceneTransformCache(document, previews);
  const origin = preview ?? rotationOriginLocalPoint(node, transformCache);
  const worldOrigin = rotationOriginVisualPointToWorld(node, transformCache, origin);
  return {
    nodeId,
    x: worldOrigin.x,
    y: worldOrigin.y,
    active,
    hovered,
    snapPoints: preview?.snapPoints.map(point => rotationOriginVisualPointToWorld(node, transformCache, point)),
  };
}

export function hitTestRotationOrigin(
  document: CurvaDocument,
  visibleNodeId: string | undefined,
  selectedNodeIds: string[],
  point: { x: number; y: number },
  zoom: number,
  previews: RenderPreview[] = [],
): { node: SceneNode; origin: { x: number; y: number } } | undefined {
  if (!visibleNodeId || selectedNodeIds.length !== 1 || selectedNodeIds[0] !== visibleNodeId) {
    return undefined;
  }

  const node = getAllSceneNodes(document).find(candidate => candidate.id === visibleNodeId);
  if (!node || node.transform.rotation === 0) {
    return undefined;
  }

  const transformCache = createSceneTransformCache(document, previews);
  const origin = rotationOriginLocalPoint(node, transformCache);
  const worldOrigin = rotationOriginVisualPointToWorld(node, transformCache, origin);
  return Math.hypot(point.x - worldOrigin.x, point.y - worldOrigin.y) <= 10 / zoom ? { node, origin } : undefined;
}

export function rotationOriginSnapPoints(document: CurvaDocument, node: SceneNode, previews: RenderPreview[] = []): Array<{ x: number; y: number }> {
  const transformCache = createSceneTransformCache(document, previews);
  const bounds = transformCache.localBounds(node);
  const origin = rotationOriginLocalPoint(node, transformCache);
  const rotation = nodeSelfRotation(node, transformCache.previewFor(node.id));
  const x1 = bounds.x;
  const y1 = bounds.y;
  const x2 = bounds.x + bounds.width;
  const y2 = bounds.y + bounds.height;
  const cx = bounds.x + bounds.width / 2;
  const cy = bounds.y + bounds.height / 2;
  return [
    { x: x1, y: y1 },
    { x: cx, y: y1 },
    { x: x2, y: y1 },
    { x: x2, y: cy },
    { x: x2, y: y2 },
    { x: cx, y: y2 },
    { x: x1, y: y2 },
    { x: x1, y: cy },
    { x: cx, y: cy },
  ].map(point => rotatePoint(point, origin, rotation));
}

function rotationOriginLocalPoint(node: SceneNode, transformCache: SceneTransformCache): { x: number; y: number } {
  return nodeRotationOrigin(node, transformCache.previewFor(node.id));
}

function rotationOriginVisualPointToWorld(node: SceneNode, transformCache: SceneTransformCache, point: { x: number; y: number }): { x: number; y: number } {
  const origin = rotationOriginLocalPoint(node, transformCache);
  const rotation = nodeSelfRotation(node, transformCache.previewFor(node.id));
  return transformCache.pointToWorld(node.id, inverseRotatePoint(point, origin, rotation));
}

export function snapRotationOrigin(point: { x: number; y: number }, snapPoints: Array<{ x: number; y: number }>, zoom: number): { x: number; y: number } {
  const threshold = 10 / zoom;
  const closest = snapPoints.reduce<{ point: { x: number; y: number }; distance: number } | undefined>((best, snapPoint) => {
    const distance = Math.hypot(point.x - snapPoint.x, point.y - snapPoint.y);
    return !best || distance < best.distance ? { point: snapPoint, distance } : best;
  }, undefined);

  return closest && closest.distance <= threshold ? closest.point : point;
}

export function vectorEditLocalPoint(document: CurvaDocument, nodeId: string, point: { x: number; y: number }): { x: number; y: number } {
  return nodeSelectionPointToLocal(document, nodeId, point);
}

export function deepSelectModifier(event: { metaKey: boolean; ctrlKey: boolean }): boolean {
  return event.metaKey || event.ctrlKey;
}

export function findDeepestFrameAtPoint(
  document: CurvaDocument,
  point: { x: number; y: number },
  activePageId?: string,
  viewportBounds?: Rect,
): FrameNode | undefined {
  const transformCache = createSceneTransformCache(document);
  return findDeepestFrameInNodes(
    getActivePageSceneNodes(document, activePageId),
    point,
    document,
    transformCache,
    viewportBounds,
  );
}

function findDeepestFrameInNodes(
  nodes: SceneNode[],
  point: { x: number; y: number },
  document: CurvaDocument,
  transformCache: ReturnType<typeof createSceneTransformCache>,
  viewportBounds?: Rect,
): FrameNode | undefined {
  for (let index = nodes.length - 1; index >= 0; index -= 1) {
    const node = nodes[index];
    if (!node || node.type !== "frame" || !node.visible || isNodeLockedForEditing(document, node.id)) {
      continue;
    }

    if (viewportBounds) {
      const subtree = transformCache.subtreeInteractionBounds(node.id);
      if (subtree && !rectsIntersect(subtree, viewportBounds)) {
        continue;
      }
    }

    if (!pointInBounds(point, transformCache.interactionBounds(node))) {
      continue;
    }

    const nested = findDeepestFrameInNodes(node.children, point, document, transformCache, viewportBounds);
    return nested ?? node;
  }

  return undefined;
}

function rectsIntersect(left: Rect, right: Rect): boolean {
  return (
    left.x <= right.x + right.width &&
    left.x + left.width >= right.x &&
    left.y <= right.y + right.height &&
    left.y + left.height >= right.y
  );
}

export function findTopFrameAtPoint(document: CurvaDocument, point: { x: number; y: number }, activePageId?: string): SceneNode | undefined {
  return [...getActivePageSceneNodes(document, activePageId)]
    .reverse()
    .find(node => node.type === "frame" && !isNodeLockedForEditing(document, node.id) && pointInBounds(point, node.bounds));
}

export function animationFrameDragModeForPoint(source: SceneNode | undefined, point: { x: number; y: number }): "marquee" | "animationObjectLink" | undefined {
  if (source?.type !== "frame") {
    return undefined;
  }

  return pointInBounds(point, source.bounds) ? "marquee" : "animationObjectLink";
}

export function pointInBounds(point: { x: number; y: number }, bounds: Rect): boolean {
  const x1 = Math.min(bounds.x, bounds.x + bounds.width);
  const x2 = Math.max(bounds.x, bounds.x + bounds.width);
  const y1 = Math.min(bounds.y, bounds.y + bounds.height);
  const y2 = Math.max(bounds.y, bounds.y + bounds.height);
  return point.x >= x1 && point.x <= x2 && point.y >= y1 && point.y <= y2;
}

export function normalizeWheelDelta(value: number, deltaMode: number): number {
  if (deltaMode === WheelEvent.DOM_DELTA_LINE) {
    return value * 16;
  }

  if (deltaMode === WheelEvent.DOM_DELTA_PAGE) {
    return value * 800;
  }

  return value;
}

export function documentPointFromScreen(canvas: HTMLCanvasElement, camera: ReturnType<typeof createDefaultCamera>, clientX: number, clientY: number): { x: number; y: number } {
  return screenToDocument(clientX, clientY, canvas, camera);
}

function directFlexParentFrameInNodes(nodes: SceneNode[], nodeId: string): FrameNode | undefined {
  for (const node of nodes) {
    const children = getSceneNodeChildren(node);
    if (!children) {
      continue;
    }

    if (node.type === "frame" && frameAutoLayoutMode(node) === "flex" && children.some(child => child.id === nodeId)) {
      return node;
    }

    const nested = directFlexParentFrameInNodes(children, nodeId);
    if (nested) {
      return nested;
    }
  }
  return undefined;
}

export function snapToPixelGridValue(value: number): number {
  return Math.round(value * 2) / 2;
}
